import React, { memo, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import classNames, { Argument as ClassName } from 'classnames'

import Text from '../../../ui/Text'
import { Dropdown } from '../../uikit/Dropdown'
import { loadBalancerItPhaseOptions, singlePhaseOptions, Phases } from '../../domain/electrical-settings'
import { EarthingSystem, LoadBalancerGroup } from '../../redux/api/web-api'
import { InfoIcon } from '../../../lib/icons/info-icon'
import { EarthingSchemeLB } from '../../enums/earthingSchemes'
import useText from '../../hooks/useText'
import { SelectButton } from '../../uikit/SelectButton'

export type LoadbalancerPhaseSettingsWidgetProps = {
    earthingScheme?: EarthingSystem | null
    setEarthingScheme: (earthingScheme: EarthingSystem) => void
    loadbalancerGroup?: LoadBalancerGroup
    hasChargers?: boolean
    showInfo?: boolean
    customStyling?: boolean
    setEnableSave?: (enableSave: boolean) => void
    setPhasesValid: (isValid: boolean) => void
    setAllowedPhases: (phase?: string | null) => void
    isEarthingSystemLocked?: boolean
    classNameSubHeading?: ClassName
    classNameComponent?: ClassName
}

export const LoadbalancerPhaseSettingsWidget = memo(function LoadbalancerPhaseSettingsWidget({
    earthingScheme,
    setEarthingScheme,
    hasChargers,
    loadbalancerGroup,
    showInfo,
    setAllowedPhases,
    setEnableSave,
    classNameSubHeading,
    classNameComponent,
    isEarthingSystemLocked,
    setPhasesValid,
}: LoadbalancerPhaseSettingsWidgetProps): React.ReactElement {
    const text = useText()
    const itNetPhase = Phases.L1L2L3
    const [tnPhase, setTnPhase] = useState<string | null | undefined>(Phases.L1L2L3)
    const {
        control: phaseControl,
        watch,
        setValue,
        trigger,
        formState: { errors: phaseErrors, isValid: isPhaseValid },
    } = useForm<Record<string, string | null | undefined>>({
        mode: 'onChange',
    })

    const { phase1, phase2, phase3 } = watch()
    const earthingSchemeBasedPhases = earthingScheme === EarthingSchemeLB.IT ? itNetPhase : tnPhase
    const getPhase1 = () =>
        loadbalancerGroup?.allowedPhases ? loadbalancerGroup?.allowedPhasesParsed?.at(0) : Phases.L1
    const getPhase2 = () =>
        loadbalancerGroup?.allowedPhases ? loadbalancerGroup?.allowedPhasesParsed?.at(1) : Phases.L2
    const getPhase3 = () =>
        loadbalancerGroup?.allowedPhases ? loadbalancerGroup?.allowedPhasesParsed?.at(2) : Phases.L3

    useEffect(() => {
        if (earthingScheme === EarthingSchemeLB.TN) {
            setValue('phase1', getPhase1())
            setValue('phase2', getPhase2())
            setValue('phase3', getPhase3())
        } else {
            setValue('itnetPhase', itNetPhase)
        }
        trigger()
    }, [earthingScheme])

    useEffect(() => {
        setTnPhase(phase1 + (phase2 ? ',' + phase2 : '') + (phase3 ? ',' + phase3 : ''))
    }, [phase1, phase2, phase3])

    useEffect(() => {
        setPhasesValid(isPhaseValid)
        setAllowedPhases(earthingSchemeBasedPhases)
    }, [earthingSchemeBasedPhases, isPhaseValid, setAllowedPhases, setPhasesValid])

    const baseClassNamesSubHeading = 'flex flex-column'
    const baseClassNamesComponent = 'flex flex-column gap-4'

    const dynamicClassNamesSubHeading = classNames(classNameSubHeading, baseClassNamesSubHeading)
    const dynamicClassNamesComponent = classNames(classNameComponent, baseClassNamesComponent)

    const isDisabledEarthingSystem = () => {
        if (isEarthingSystemLocked) return text('msgEarthingSystemisLocked')
        if (loadbalancerGroup?.active) return text('msgLbActive')
        if (hasChargers) return text('msgDisabledEarthingSystemHasChargers')
    }

    return (
        <div className={dynamicClassNamesComponent}>
            <div className={dynamicClassNamesSubHeading}>
                <div className="flex justify-content-between">
                    <label className="text-sm mt-3 text-gray-600">
                        <Text code="lblEarthingSystem" />
                    </label>
                    {showInfo && (
                        <span className="mt-3">
                            <InfoIcon
                                data-testid="earthing-info-icon"
                                title={text('msgEarthingSystemInfoLB')}
                                size={22}
                            />
                        </span>
                    )}
                </div>
                <SelectButton<EarthingSystem>
                    id="earthing-system"
                    ariaLabel={text('lblEarthingSystem')}
                    value={earthingScheme}
                    options={[
                        {
                            label: text('lblTN'),
                            value: 'TN',
                        },
                        {
                            label: text('lblITNet'),
                            value: 'IT',
                        },
                    ]}
                    onChange={(nextEarthingScheme) => {
                        setEarthingScheme(nextEarthingScheme)
                        setEnableSave?.(true)
                    }}
                    disabled={isEarthingSystemLocked || loadbalancerGroup?.active || hasChargers}
                    tooltip={isDisabledEarthingSystem()}
                />
            </div>

            {earthingScheme && (
                <div className={dynamicClassNamesSubHeading}>
                    <div className="flex justify-content-between">
                        <label className="text-sm mt-3 text-gray-600">
                            <Text code="lblPhaseSettings" />
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        {showInfo && (
                            <span className="mt-3">
                                <InfoIcon
                                    data-testid="phase-info-icon"
                                    title={text('lblPhaseSettingsInfoLB')}
                                    size={22}
                                />
                            </span>
                        )}
                    </div>
                    <div className="flex flex-column gap-3">
                        {earthingScheme === EarthingSchemeLB.IT ? (
                            <div className="w-full">
                                <Controller
                                    name="itnetPhase"
                                    control={phaseControl}
                                    render={({ field: { value, ...rest } }) => (
                                        <Dropdown
                                            data-testid="itNet-combobox"
                                            id="itnetPhase"
                                            options={loadBalancerItPhaseOptions}
                                            {...rest}
                                            disabled={true}
                                            disabledReason={text('msgDisabledITnetPhaseModes')}
                                            value={value}
                                            showClear={false}
                                        />
                                    )}
                                />
                            </div>
                        ) : (
                            <div className="flex flex-row w-full gap-3">
                                <div className="flex-1 min-w-0">
                                    <Controller
                                        name="phase1"
                                        control={phaseControl}
                                        rules={{
                                            required: text('msgRequiredShort'),
                                            validate: (val) => earthingScheme === 'TN' && val !== null,
                                        }}
                                        render={({ field: { value, onChange, ...rest } }) => (
                                            <Dropdown
                                                disabled={loadbalancerGroup?.active}
                                                data-testid="phase-1-combobox"
                                                label={text('lblPhaseNo', { phaseNo: '1' })}
                                                id="phase1"
                                                options={singlePhaseOptions}
                                                {...rest}
                                                value={value}
                                                onChange={(e) => {
                                                    onChange(e.value)
                                                    setValue('phase2', null)
                                                    setValue('phase3', null)
                                                    setEnableSave?.(true)
                                                }}
                                                error={phaseErrors.phase1?.message}
                                                showClear={false}
                                            />
                                        )}
                                    />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <Controller
                                        name="phase2"
                                        control={phaseControl}
                                        render={({ field: { value, onChange, ...rest } }) => (
                                            <Dropdown
                                                data-testid="phase-2-combobox"
                                                label={text('lblPhaseNo', { phaseNo: '2' })}
                                                id="phase2"
                                                options={singlePhaseOptions.filter((phase) => phase.value !== phase1)}
                                                {...rest}
                                                value={value}
                                                onChange={(e) => {
                                                    onChange(e.value)
                                                    setValue('phase3', null)
                                                    setEnableSave?.(true)
                                                }}
                                                disabled={!phase1 || loadbalancerGroup?.active}
                                                error={phaseErrors.phase2?.message}
                                                showClear
                                            />
                                        )}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <Controller
                                        name="phase3"
                                        control={phaseControl}
                                        render={({ field: { value, onChange, ...rest } }) => (
                                            <Dropdown
                                                data-testid="phase-3-combobox"
                                                label={text('lblPhaseNo', { phaseNo: '3' })}
                                                id="phase3"
                                                options={singlePhaseOptions.filter(
                                                    (phase) => phase.value !== phase1 && phase.value !== phase2
                                                )}
                                                {...rest}
                                                value={value}
                                                onChange={(e) => {
                                                    onChange(e.value)
                                                    setEnableSave?.(true)
                                                }}
                                                disabled={!phase2 || loadbalancerGroup?.active}
                                                error={phaseErrors.phase3?.message}
                                                showClear
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
})
